import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Chip,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Avatar,
  useMediaQuery,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

// Icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GrassIcon from "@mui/icons-material/Grass";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GrainIcon from '@mui/icons-material/Grain';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import PublicIcon from '@mui/icons-material/Public';
import ScienceIcon from '@mui/icons-material/Science';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import OpacityIcon from '@mui/icons-material/Opacity';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import KitchenIcon from '@mui/icons-material/Kitchen';
import SpaIcon from '@mui/icons-material/Spa';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NatureIcon from "@mui/icons-material/Nature";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// Styled components
const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  minWidth: 120,
  padding: '14px 20px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: theme.palette.primary.main,
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

const GradientTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3.5),
  height: '100%',
  borderRadius: 20,
  boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
  transition: 'all 0.4s ease',
  border: '1px solid rgba(0,0,0,0.03)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
    '& .feature-icon-bg': {
      transform: 'scale(1.1)',
    }
  },
}));

const CircleAvatar = styled(Avatar)(({ theme }) => ({
  width: 65,
  height: 65, 
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.dark,
  marginBottom: theme.spacing(2.5),
  transition: 'all 0.3s ease',
  boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
}));

const NutrientCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  borderRadius: 16,
  boxShadow: '0 8px 25px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  width: '60px',
  height: '4px',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  borderRadius: '2px',
}));

// Data
const sunflowerOilNutritionData = [
  { nutrient: { tr: 'Enerji', en: 'Energy' }, value: '884 kcal', unit: '100g', icon: <RestaurantIcon fontSize="large" /> },
  { nutrient: { tr: 'Yağ', en: 'Fat' }, value: '100g', unit: '100g', icon: <OpacityIcon fontSize="large" /> },
  { nutrient: { tr: 'Omega-6', en: 'Omega-6' }, value: '65g', unit: '100g', icon: <ScienceIcon fontSize="large" /> },
  { nutrient: { tr: 'Vitamin E', en: 'Vitamin E' }, value: '41mg', unit: '100g', icon: <HealthAndSafetyIcon fontSize="large" /> },
  { nutrient: { tr: 'Doymuş Yağ', en: 'Saturated Fat' }, value: '11g', unit: '100g', icon: <WaterDropIcon fontSize="large" /> },
  { nutrient: { tr: 'Tekli Doymamış', en: 'Monounsaturated' }, value: '20g', unit: '100g', icon: <WaterDropIcon fontSize="large" /> },
  { nutrient: { tr: 'Çoklu Doymamış', en: 'Polyunsaturated' }, value: '65g', unit: '100g', icon: <WaterDropIcon fontSize="large" /> },
  { nutrient: { tr: 'Kolesterol', en: 'Cholesterol' }, value: '0mg', unit: '100g', icon: <HealthAndSafetyIcon fontSize="large" /> },
];

const sunflowerOilQualityStats = [
  {
    name: { tr: "Vitamin E", en: "Vitamin E" },
    value: "41mg",
    icon: <HealthAndSafetyIcon />,
    description: { 
      tr: "Yüksek E vitamini içeriği, güçlü antioksidan etkisi sağlar.", 
      en: "High vitamin E content provides strong antioxidant effect."
    },
  },
  {
    name: { tr: "Saflık", en: "Purity" },
    value: "99.9%",
    icon: <WaterDropIcon />,
    description: { 
      tr: "Yüksek saflık oranı, premium kalite garantisidir.", 
      en: "High purity rate guarantees premium quality."
    },
  },
  {
    name: { tr: "Duman Noktası", en: "Smoke Point" },
    value: "232°C",
    icon: <LocalFireDepartmentIcon />,
    description: { 
      tr: "Yüksek ısıya dayanıklılık, pişirme için mükemmel.", 
      en: "High heat resistance, perfect for cooking."
    },
  },
  {
    name: { tr: "Raf Ömrü", en: "Shelf Life" },
    value: "18 Ay",
    icon: <KitchenIcon />,
    description: { 
      tr: "Uzun raf ömrü ile tazeliğini ve kalitesini korur.", 
      en: "Maintains freshness and quality with long shelf life."
    },
  },
];

const sunflowerOilFeatures = [
  {
    title: { tr: "Rafine Kalite", en: "Refined Quality" },
    description: {
      tr: "Gelişmiş rafinasyon yöntemleriyle elde edilen, hafif ve nötr aromalı ayçiçek yağı.",
      en: "Sunflower oil with a light and neutral flavor, obtained through advanced refining methods."
    
  },
    icon: <OpacityIcon fontSize="large" />,
    color: "#4CAF50",
    bgColor: "#E8F5E9",
  },
  {
    title: { tr: "Hafif Tat", en: "Light Taste" },
    description: {
      tr: "Gıdaların orijinal lezzetini bozmayan hafif ve nötr tat profili.",
      en: "Light and neutral taste profile that doesn't alter the original flavor of foods."
    },
    icon: <RestaurantIcon fontSize="large" />,
    color: "#2196F3",
    bgColor: "#E3F2FD",
  },
  {
    title: { tr: "Vitamin Kaynağı", en: "Vitamin Source" },
    description: { 
      tr: "Yüksek E vitamini içeriği ile antioksidan özelliği taşır.",
      en: "Has antioxidant properties with high vitamin E content."
    },
    icon: <HealthAndSafetyIcon fontSize="large" />,
    color: "#FFC107",
    bgColor: "#FFF8E1",
  },
  {
    title: { tr: "Sürdürülebilir", en: "Sustainable" },
    description: {
      tr: "Sürdürülebilir tarım uygulamalarıyla yetiştirilen ayçiçeklerden üretilir.",
      en: "Produced from sunflowers grown with sustainable farming practices."
    },
    icon: <NatureIcon fontSize="large" />,
    color: "#9C27B0",
    bgColor: "#F3E5F5",
  },
];

// İlgili ürünler listesi
const productItems = [
  {
    id: "wheat",
    name: { tr: "Buğday", en: "Wheat" },
    image: "/images/wheat.jpeg",
  },
  {
    id: "corn",
    name: { tr: "Mısır", en: "Corn" },
    image: "/images/corn.jpeg",
  },
  {
    id: "corn-oil",
    name: { tr: "Mısır Yağı", en: "Corn Oil" },
    image: "/images/corn-oil.jpeg",
  },
  {
    id: "barley",
    name: { tr: "Arpa", en: "Barley" },
    image: "/images/barley.jpeg",
  },
  {
    id: "chicken",
    name: { tr: "Tavuk", en: "Chicken" },
    image: "/images/chicken.jpeg",
  },
];

const SunflowerOilPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [tabValue, setTabValue] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Sayfa başlığını güncelle
  useEffect(() => {
    document.title =
      "SGT Global Trade | " +
      (i18n.language === "tr" ? "Ayçiçek Yağı" : "Sunflower Oil");

    // Image preload
    const img = new Image();
    img.src = "/images/sunflower-oil.jpeg";
    img.onload = () => setImageLoaded(true);

    // Sayfa yüklendiğinde en üste kaydır
    window.scrollTo(0, 0);
  }, [i18n.language]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FCFCFC",
        minHeight: "100vh",
        pt: { xs: 3, md: 5 },
        pb: 8,
      }}
      id="top"
    >
      {/* Hero Section - Flexbox Approach */}
      <Container maxWidth="xl">
        <Box
          sx={{
            my: 4,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            alignItems: "center",
          }}
        >
          {/* Left Side - Content */}
          <Box sx={{ flex: "1 1 50%" }}>
            <Chip
              icon={<OpacityIcon />}
              label={
                i18n.language === "tr"
                  ? "Premium Bitkisel Yağ"
                  : "Premium Vegetable Oil"
              }
              color="primary"
              sx={{
                borderRadius: "8px",
                py: 0.5,
                px: 1,
                mb: 2,
              }}
            />

            <GradientTypography
              variant="h2"
              component="h1"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                mb: 2,
              }}
            >
              {i18n.language === "tr"
                ? "Saf Ayçiçek Yağı"
                : "Pure Sunflower Oil"}
            </GradientTypography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              {i18n.language === "tr"
                ? "Ayçiçek yağı, ayçiçeği tohumlarından elde edilen, sağlıklı ve çok yönlü bir bitkisel yağdır. E vitamini bakımından zengin olan ayçiçek yağı, hafif tadı ve yüksek ısı dayanıklılığı ile mutfaklarda tercih edilir. Doymamış yağ asitleri bakımından zengin yapısıyla dengeli beslenmenin bir parçasıdır."
                : "Sunflower oil is a healthy and versatile vegetable oil obtained from sunflower seeds. Rich in vitamin E, sunflower oil is preferred in kitchens for its light taste and high heat resistance. With its structure rich in unsaturated fatty acids, it is part of a balanced diet."}
            </Typography>

            {/* Stats */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {sunflowerOilQualityStats.map((stat, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: "1 1 calc(50% - 8px)",
                    "@media (min-width: 600px)": {
                      flex: "1 1 calc(25% - 12px)",
                    },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      textAlign: "center",
                      backgroundColor: "white",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.primary.main,
                        margin: "0 auto",
                        mb: 1,
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        fontSize: "1.25rem",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {i18n.language === "tr" ? stat.name.tr : stat.name.en}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Side - Image */}
          <Box sx={{ flex: "1 1 50%" }}>
            <Box
              sx={{
                position: "relative",
                height: { xs: "300px", md: "450px" },
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              }}
            >
              {!imageLoaded && (
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#f5f5f5",
                  }}
                >
                  <CircularProgress color="primary" />
                </Box>
              )}

              {imageLoaded && (
                <img
                  src="/images/sunflower-oil.jpeg"
                  alt="Ayçiçek Yağı"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}

              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                  p: 3,
                  color: "white",
                }}
              >
                <Typography variant="h5" fontWeight={600}>
                  {i18n.language === "tr"
                    ? "Premium Ayçiçek Yağı"
                    : "Premium Sunflower Oil"}
                </Typography>
                <Typography variant="body1">
                  {i18n.language === "tr"
                    ? "Yüksek kaliteli, saf ve doğal"
                    : "High-quality, pure and natural"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Main Content Section with Tabs */}
      <Container maxWidth="xl" sx={{ mt: 8 }}>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 5,
              mx: "auto",
              maxWidth: 800,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              centered={!isMobile}
              sx={{
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: "2px",
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              <StyledTab
                icon={<DescriptionIcon />}
                iconPosition="start"
                label={i18n.language === "tr" ? "Özellikler" : "Features"}
              />
              <StyledTab
                icon={<BarChartIcon />}
                iconPosition="start"
                label={i18n.language === "tr" ? "Besin Değerleri" : "Nutrition"}
              />
            </Tabs>
          </Box>

          {/* Features Tab - REDESIGNED */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ py: 2 }}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={0}
              >
                <Typography
                  variant="h3"
                  component="h2"
                  align="center"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.5rem" },
                  }}
                >
                  {i18n.language === "tr"
                    ? "Ayçiçek Yağı Özellikleri"
                    : "Sunflower Oil Characteristics"}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <StyledDivider />
                </Box>

                <Typography
                  variant="h6"
                  component="p"
                  align="center"
                  color="text.secondary"
                  sx={{
                    maxWidth: "800px",
                    mx: "auto",
                    mb: 6,
                    fontWeight: 400,
                  }}
                >
                  {i18n.language === "tr"
                    ? "Ayçiçek yağı, zengin vitamin içeriği ve yüksek ısı dayanıklılığı ile mutfaklarda ideal bir seçimdir."
                    : "Sunflower oil is an ideal choice in kitchens with its rich vitamin content and high heat resistance."}
                </Typography>

                <Grid container spacing={4} justifyContent="center">
                  {sunflowerOilFeatures.map((feature, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        custom={index * 0.2}
                        style={{ height: "100%" }}
                      >
                        <FeatureCard sx={{ height: "100%" }}>
                          <Box
                            className="feature-icon-bg"
                            sx={{
                              position: "absolute",
                              top: "-20px",
                              right: "-20px",
                              width: "120px",
                              height: "120px",
                              borderRadius: "50%",
                              backgroundColor: feature.bgColor,
                              zIndex: 0,
                              transition: "all 0.4s ease",
                            }}
                          />

                          <CircleAvatar
                            sx={{
                              backgroundColor: feature.bgColor,
                              color: feature.color,
                              zIndex: 1,
                            }}
                          >
                            {feature.icon}
                          </CircleAvatar>

                          <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                              fontWeight: 700,
                              position: "relative",
                              zIndex: 1,
                              color: feature.color,
                            }}
                          >
                            {i18n.language === "tr"
                              ? feature.title.tr
                              : feature.title.en}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              flexGrow: 1,
                              position: "relative",
                              zIndex: 1,
                              fontSize: "0.95rem",
                              lineHeight: 1.6,
                            }}
                          >
                            {i18n.language === "tr"
                              ? feature.description.tr
                              : feature.description.en}
                          </Typography>
                        </FeatureCard>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 8 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 5 },
                      borderRadius: "24px",
                      backgroundColor: "#F9FAFB",
                      border: "1px solid rgba(0,0,0,0.05)",
                      boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: { xs: "100%", md: "40%" },
                        opacity: { xs: 0.05, md: 0.1 },
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        zIndex: 0,
                      }}
                    />

                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        gutterBottom
                        fontWeight={700}
                        color="primary"
                      >
                        {i18n.language === "tr"
                          ? "Ayçiçek Yağı Hakkında"
                          : "About Sunflower Oil"}
                      </Typography>

                      <StyledDivider sx={{ mb: 4 }} />

                      <Typography
                        variant="body1"
                        sx={{ mb: 3, lineHeight: 1.8 }}
                      >
                        {i18n.language === "tr"
                          ? "Ayçiçek yağı, ayçiçeği (Helianthus annuus) tohumlarından elde edilen, açık sarı renkli, hafif kokulu bir bitkisel yağdır. Düşük doymuş yağ ve yüksek doymamış yağ içeriği ile sağlıklı bir beslenme profili sunar. Yüksek ısıya dayanıklılığı ve nötr tadı sayesinde geniş bir kullanım alanına sahiptir."
                          : "Sunflower oil is a light yellow, mildly scented vegetable oil obtained from sunflower (Helianthus annuus) seeds. It offers a healthy nutritional profile with low saturated fat and high unsaturated fat content. It has a wide range of uses due to its high heat resistance and neutral taste."}
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {i18n.language === "tr"
                          ? "E vitamini bakımından son derece zengin olan ayçiçek yağı, güçlü bir antioksidan etkiye sahiptir. Çoklu doymamış yağ asitleri içeriği, özellikle linoleik asit (Omega-6) bakımından zengindir. Bu özellikleri ile hem pişirme, kızartma ve salatalarda kullanım için idealdir hem de kalp sağlığına olumlu etkileri ile bilinir."
                          : "Extremely rich in vitamin E, sunflower oil has a strong antioxidant effect. Its polyunsaturated fatty acid content is especially rich in linoleic acid (Omega-6). With these properties, it is ideal for use in cooking, frying, and salads, and is known for its positive effects on heart health."}
                      </Typography>

                      <Grid container spacing={3} sx={{ mt: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "primary.light",
                                color: "primary.dark",
                                mb: 1.5,
                              }}
                            >
                              <RestaurantIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Mutfak Kullanımı"
                                : "Culinary Use"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "Pişirme, kızartma ve salatalarda"
                                : "In cooking, frying, and salads"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "secondary.light",
                                color: "secondary.dark",
                                mb: 1.5,
                              }}
                            >
                              <SpaIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Kozmetik Kullanım"
                                : "Cosmetic Use"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "Cilt bakım ürünleri ve sabunlarda"
                                : "In skin care products and soaps"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.info.light,
                                color: theme.palette.info.dark,
                                mb: 1.5,
                              }}
                            >
                              <FavoriteIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Kalp Sağlığı"
                                : "Heart Health"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "Doymamış yağ asitleri ile kalp dostu"
                                : "Heart-friendly with unsaturated fatty acids"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.dark,
                                mb: 1.5,
                              }}
                            >
                              <WbSunnyIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Güneş Koruması"
                                : "Sun Protection"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "E vitamini ile güneş hasarına karşı koruma"
                                : "Protection against sun damage with vitamin E"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Box>
              </motion.div>
            </Box>
          </TabPanel>

          {/* Nutrition Tab - REDESIGNED */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ py: 2 }}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={0}
              >
                <Typography
                  variant="h3"
                  component="h2"
                  align="center"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.5rem" },
                  }}
                >
                  {i18n.language === "tr"
                    ? "Besin Değerleri"
                    : "Nutritional Values"}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <StyledDivider />
                </Box>

                <Typography
                  variant="h6"
                  component="p"
                  align="center"
                  color="text.secondary"
                  sx={{
                    maxWidth: "800px",
                    mx: "auto",
                    mb: 6,
                    fontWeight: 400,
                  }}
                >
                  {i18n.language === "tr"
                    ? "Ayçiçek yağı, E vitamini ve doymamış yağ asitleri bakımından oldukça zengindir."
                    : "Sunflower oil is very rich in vitamin E and unsaturated fatty acids."}
                </Typography>

                {/* Nutrient Cards in Grid - Modern Design */}
                <Grid container spacing={4}>
                  {/* Table - Left Column */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, md: 4 },
                        borderRadius: "20px",
                        backgroundColor: "white",
                        boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
                        height: "100%",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "8px",
                          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        }}
                      />

                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        fontWeight={700}
                        color="primary"
                        sx={{ mt: 1 }}
                      >
                        {i18n.language === "tr"
                          ? "Besin Değerleri Tablosu"
                          : "Nutritional Values Table"}
                      </Typography>

                      <TableContainer sx={{ mt: 3 }}>
                        <Table aria-label="besin değerleri tablosu">
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: theme.palette.primary.light,
                                "& th": {
                                  fontWeight: 700,
                                  color: theme.palette.primary.dark,
                                  fontSize: "0.95rem",
                                },
                              }}
                            >
                              <TableCell>
                                {i18n.language === "tr" ? "Besin" : "Nutrient"}
                              </TableCell>
                              <TableCell align="right">
                                {i18n.language === "tr" ? "Değer" : "Value"}
                              </TableCell>
                              <TableCell align="right">
                                {i18n.language === "tr" ? "Birim" : "Unit"}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sunflowerOilNutritionData.map((row, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:nth-of-type(even)": {
                                    backgroundColor: "#f8f9fa",
                                  },
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.primary.lighter ||
                                      "#e3f2fd",
                                  },
                                }}
                              >
                                <TableCell
                                  component="th"
                                  scope="row"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {i18n.language === "tr"
                                    ? row.nutrient.tr
                                    : row.nutrient.en}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  {row.value}
                                </TableCell>
                                <TableCell align="right">{row.unit}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>

                  {/* Health Benefits - Right Column */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, md: 4 },
                        borderRadius: "20px",
                        backgroundColor: "white",
                        boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "8px",
                          background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        }}
                      />

                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        fontWeight={700}
                        color="primary"
                        sx={{ mt: 1 }}
                      >
                        {i18n.language === "tr"
                          ? "Sağlık Faydaları"
                          : "Health Benefits"}
                      </Typography>

                      <List>
                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.primary.lighter || "#e3f2fd",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.primary.light,
                                color: theme.palette.primary.dark,
                              }}
                            >
                              <FavoriteIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "Kalp Sağlığı"
                                  : "Heart Health"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "Doymamış yağ asitleri içeriği ile kolesterol dengesine yardımcı olur"
                                  : "Helps cholesterol balance with its unsaturated fatty acid content"}
                              </Typography>
                            }
                          />
                        </ListItem>

                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.secondary.lighter || "#e8f5e9",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.secondary.light,
                                color: theme.palette.secondary.dark,
                              }}
                            >
                              <HealthAndSafetyIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "Antioksidan Etkisi"
                                  : "Antioxidant Effect"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "Yüksek E vitamini ile hücreleri serbest radikallerden korur"
                                  : "Protects cells from free radicals with high vitamin E"}
                              </Typography>
                            }
                          />
                        </ListItem>

                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.info.lighter || "#e3f2fd",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.info.light,
                                color: theme.palette.info.dark,
                              }}
                            >
                              <ScienceIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "Bağışıklık Sistemi"
                                  : "Immune System"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "İçerdiği vitaminler ile bağışıklık sistemini destekler"
                                  : "Supports the immune system with the vitamins it contains"}
                              </Typography>
                            }
                          />
                        </ListItem>

                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.warning.lighter || "#fff8e1",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.dark,
                              }}
                            >
                              <SpaIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "Cilt Sağlığı"
                                  : "Skin Health"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "Cilt hücrelerini besler ve nemlendirici özellik taşır"
                                  : "Nourishes skin cells and has moisturizing properties"}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      backgroundColor: "white",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={12} md={7}>
                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color="primary"
                            sx={{ mb: 0.5 }}
                          >
                            {i18n.language === "tr"
                              ? "Sağlıklı Yaşam İçin Ayçiçek Yağı"
                              : "Sunflower Oil for Healthy Living"}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.85rem", lineHeight: 1.6 }}
                          >
                            {i18n.language === "tr"
                              ? "Yüksek E vitamini ve doymamış yağ asitleri içeren ayçiçek yağı, dengeli beslenmenin bir parçası olarak günlük diyetinize dahil edilebilir. Kalp sağlığını destekler ve çok yönlü kullanım sunar."
                              : "Sunflower oil, which contains high vitamin E and unsaturated fatty acids, can be included in your daily diet as part of a balanced nutrition. It supports heart health and offers versatile usage."}
                          </Typography>
                        </CardContent>
                      </Grid>
                    </Grid>
                  </Card>
                </Box>
              </motion.div>
            </Box>
          </TabPanel>
        </Box>
      </Container>
      {/* İlgili Ürünler Bölümü - Mobil Görünüm İçin Düzeltilmiş Versiyon */}
      <Container maxWidth="xl" sx={{ mt: 8 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          custom={0}
        >
          <Typography
            variant="h3"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
            }}
          >
            {i18n.language === "tr" ? "İlgili Ürünler" : "Related Products"}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <StyledDivider />
          </Box>

          <Typography
            variant="h6"
            component="p"
            align="center"
            color="text.secondary"
            sx={{
              maxWidth: "800px",
              mx: "auto",
              mb: 6,
              fontWeight: 400,
            }}
          >
            {i18n.language === "tr"
              ? "Diğer tahıl ve tarım ürünlerimizi inceleyin"
              : "Explore our other grain and agricultural products"}
          </Typography>

          {/* Düzeltilmiş kaydırma alanı ve grid yapısı */}
          <Box
            sx={{
              overflowX: "auto",
              pb: 2,
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.1)",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0,0,0,0.02)",
              },
              WebkitOverflowScrolling: "touch", // Daha akıcı kaydırma için
              scrollbarWidth: "thin", // Firefox için
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: { xs: "max-content", lg: "100%" },
                gap: 3,
                px: { xs: 1, md: 0 },
              }}
            >
              {productItems.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    width: { xs: "240px", sm: "280px", md: "300px", lg: "1fr" },
                    flex: { lg: "1 1 0" },
                  }}
                >
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    custom={index * 0.2}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": {
                          transform: "translateY(-10px)",
                          boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="180"
                        image={item.image}
                        alt={item.name[i18n.language === "tr" ? "tr" : "en"]}
                        sx={{
                          transition: "transform 0.5s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h6"
                          component="h3"
                          gutterBottom
                          fontWeight={600}
                        >
                          {item.name[i18n.language === "tr" ? "tr" : "en"]}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: "auto" }}
                        >
                          {i18n.language === "tr"
                            ? "Yüksek kaliteli tarım ürünlerimiz"
                            : "Our high-quality agricultural products"}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => navigate(`/products/${item.id}`)}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            mt: 2,
                            alignSelf: "flex-start",
                          }}
                        >
                          {i18n.language === "tr" ? "Detaylar" : "Details"}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Container>

      {/* Hemen Teklif Alın - Modernize edilmiş ve sayfanın en altına taşınmış */}
      <Box sx={{ my: 8, py: 4 }}>
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
          >
            <Box
              sx={{
                borderRadius: "24px",
                overflow: "hidden",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                position: "relative",
              }}
            >
              {/* Background Pattern */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  opacity: 0.1,
                  zIndex: 0,
                }}
              />

              <Grid
                container
                alignItems="center"
                sx={{ position: "relative", zIndex: 1 }}
              >
                <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 5 } }}>
                  <Typography
                    variant="h2"
                    component="h2"
                    sx={{
                      color: "white",
                      fontWeight: 800,
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      mb: 2,
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Premium Ayçiçek Yağı Tedariği"
                      : "Premium Sunflower Oil Supply"}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "white",
                      opacity: 0.9,
                      mt: 1,
                      mb: 3,
                      fontWeight: 400,
                      textShadow: "0 1px 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Rekabetçi fiyatlar ve güvenilir tedarik zinciri"
                      : "Competitive prices and reliable supply chain"}
                  </Typography>

                  <Box
                    sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 3 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 500,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Yüksek Kalite"
                          : "High Quality"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 500,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Esnek Sevkiyat"
                          : "Flexible Shipping"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 500,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Özel Çözümler"
                          : "Custom Solutions"}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/quote")}
                    sx={{
                      borderRadius: "12px",
                      py: 1.8,
                      px: 5,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      backgroundColor: "white",
                      color: theme.palette.primary.main,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                      "&:hover": {
                        backgroundColor: "white",
                        transform: "translateY(-3px)",
                        boxShadow: "0 12px 25px rgba(0,0,0,0.25)",
                      },
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Hemen Teklif Al"
                      : "Get a Quote Now"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default SunflowerOilPage;